/**
 * Shared plumbing for local-first writes: the value lands in the store now and the
 * request runs in the background, so a late reply from an older write must not
 * revert a newer one.
 */

/** Copies `keys` out of `source`, including keys `source` does not carry. */
export function pickKeys<T extends object>(source: Partial<T>, keys: (keyof T)[]): Partial<T> {
	const picked: Partial<T> = {};
	for (const key of keys) {
		picked[key] = source[key];
	}
	return picked;
}

/** Reverts a failed save unless the value changed again while it was in flight. */
export async function saveOrRevert<V>(
	save: () => Promise<boolean>,
	read: () => V,
	revert: () => void,
): Promise<boolean> {
	const savedValue = read();
	const saved = await save();
	if (!saved && read() === savedValue) {
		revert();
	}
	return saved;
}

/** An in-flight write may touch a key only while it still holds that key's newest generation. */
export class WriteGenerations<K> {
	private generations = new Map<K, number>();

	/** Claims the newest generation for each key, returning the claim to check `owns` against. */
	claim(keys: K[]): Map<K, number> {
		return new Map(
			keys.map((key) => {
				const generation = (this.generations.get(key) ?? 0) + 1;
				this.generations.set(key, generation);
				return [key, generation];
			}),
		);
	}

	owns(key: K, claim: Map<K, number>): boolean {
		const claimedGeneration = claim.get(key);
		// An unclaimed key is unowned: without this, undefined === undefined grants it.
		return claimedGeneration !== undefined && this.generations.get(key) === claimedGeneration;
	}
}
